import { Component } from "@/types";
import type { BaseChartProperties } from "@/types/chart-properties";
import { ChartMixinBase, MixinResult } from "./ChartMixinBase";

/**
 * Mixin for handling color-related commands across all chart types
 */
export class ColorMixin extends ChartMixinBase {
  private readonly colorThemes: Record<string, string[]> = {
    // Basic colors
    blue: ["#3B82F6", "#60A5FA", "#93C5FD", "#BFDBFE", "#DBEAFE"],
    red: ["#EF4444", "#F87171", "#FCA5A5", "#FECACA", "#FEE2E2"],
    green: ["#10B981", "#34D399", "#6EE7B7", "#A7F3D0", "#D1FAE5"],
    purple: ["#8B5CF6", "#A78BFA", "#C4B5FD", "#DDD6FE", "#EDE9FE"],
    orange: ["#F97316", "#FB923C", "#FDBA74", "#FED7AA", "#FFF7ED"],
    yellow: ["#F59E0B", "#FCD34D", "#FDE68A", "#FEF3C7", "#FEF9C3"],
    pink: ["#EC4899", "#F472B6", "#F9A8D4", "#FBCFE8", "#FCE7F3"],
    indigo: ["#6366F1", "#818CF8", "#A5B4FC", "#C7D2FE", "#E0E7FF"],

    // Theme palettes
    rainbow: ["#FF0000", "#FF7F00", "#FFFF00", "#00FF00", "#0000FF", "#4B0082", "#9400D3"],
    pastel: ["#FFB3BA", "#FFDFBA", "#FFFFBA", "#BAFFC9", "#BAE1FF", "#E6E6FA"],
    ocean: ["#0077BE", "#0099CC", "#00BBDD", "#00DDDD", "#5DADE2"],
    sunset: ["#FF6B6B", "#FFE66D", "#FF8E53", "#C73E1D", "#6A1B9A"],
    forest: ["#228B22", "#32CD32", "#3CB371", "#2E8B57", "#006400"],

    // Professional themes
    corporate: ["#1E40AF", "#475569", "#64748B", "#94A3B8", "#CBD5E1"],
    financial: ["#0F172A", "#1E293B", "#334155", "#475569", "#64748B"],
    medical: ["#0891B2", "#06B6D4", "#22D3EE", "#38BDF8", "#7DD3FC"],

    // Agricultural themes (domain-specific)
    agricultural: ["#8BC34A", "#689F38", "#558B2F", "#33691E", "#827717"],
    harvest: ["#FFA000", "#F57C00", "#E65100", "#BF360C", "#795548"],
    soil: ["#6D4C41", "#5D4037", "#4E342E", "#3E2723", "#8D6E63"],
    crop: ["#7CB342", "#558B2F", "#33691E", "#9CCC65", "#AED581"]
  };

  parseCommand(command: string, _component: Component): MixinResult {
    const lowerCommand = command.toLowerCase();
    const changes: Partial<BaseChartProperties> = {};

    // Check for color theme commands
    const themeMatch = this.matchColorTheme(lowerCommand);
    if (themeMatch) {
      changes.colors = themeMatch;
    }

    // Check for specific hex colors
    const hexColors = this.extractHexColors(command);
    if (hexColors.length > 0) {
      changes.colors = hexColors;
    }

    // Check for RGB/RGBA colors
    const rgbColors = this.extractRgbColors(command);
    if (rgbColors.length > 0) {
      changes.colors = changes.colors ? [...changes.colors, ...rgbColors] : rgbColors;
    }

    // Handle gradient commands
    if (this.hasKeywords(command, ['gradient', 'fade'])) {
      const gradientColors = this.parseGradientCommand(command);
      if (gradientColors) {
        changes.gradient = true;
        changes.colors = gradientColors;
      }
    }

    // Handle monochrome command
    if (this.hasKeywords(command, ['monochrome', 'grayscale', 'black and white'])) {
      changes.colors = this.generateMonochromeColors();
    }

    return Object.keys(changes).length > 0 ? changes : null;
  }

  private matchColorTheme(command: string): string[] | null {
    for (const [theme, colors] of Object.entries(this.colorThemes)) {
      if (command.includes(theme)) {
        return colors;
      }
    }

    // Check for variations
    if (command.includes('earth') || command.includes('nature')) {
      return this.colorThemes.forest;
    }
    if (command.includes('sea') || command.includes('water')) {
      return this.colorThemes.ocean;
    }
    if (command.includes('business') || command.includes('professional')) {
      return this.colorThemes.corporate;
    }
    if (command.includes('farm') || command.includes('field')) {
      return this.colorThemes.agricultural;
    }

    return null;
  }

  private extractHexColors(command: string): string[] {
    const hexPattern = /#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g;
    const matches = command.match(hexPattern);
    return matches || [];
  }

  private extractRgbColors(command: string): string[] {
    const rgbPattern = /rgba?\([^)]+\)/g;
    const matches = command.match(rgbPattern);
    return matches || [];
  }

  private parseGradientCommand(command: string): string[] | null {
    // Extract two colors for gradient
    const hexColors = this.extractHexColors(command);
    if (hexColors.length >= 2) {
      return this.generateGradient(hexColors[0], hexColors[1]);
    }

    // Check for named gradient patterns
    if (command.includes('blue gradient')) {
      return this.generateGradient('#3B82F6', '#DBEAFE');
    }
    if (command.includes('red gradient')) {
      return this.generateGradient('#EF4444', '#FEE2E2');
    }
    if (command.includes('green gradient')) {
      return this.generateGradient('#10B981', '#D1FAE5');
    }

    return null;
  }

  private generateGradient(startColor: string, endColor: string, steps: number = 5): string[] {
    const gradient: string[] = [];

    // Convert hex to RGB
    const start = this.hexToRgb(startColor);
    const end = this.hexToRgb(endColor);

    if (!start || !end) return [startColor, endColor];

    for (let i = 0; i < steps; i++) {
      const ratio = i / (steps - 1);
      const r = Math.round(start.r + (end.r - start.r) * ratio);
      const g = Math.round(start.g + (end.g - start.g) * ratio);
      const b = Math.round(start.b + (end.b - start.b) * ratio);
      gradient.push(this.rgbToHex(r, g, b));
    }

    return gradient;
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null;
  }

  private rgbToHex(r: number, g: number, b: number): string {
    return "#" + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }).join("");
  }

  private generateMonochromeColors(): string[] {
    return ["#000000", "#404040", "#808080", "#BFBFBF", "#E0E0E0"];
  }

  getSuggestions(_component: Component): string[] {
    return [
      "Use blue color theme",
      "Apply rainbow colors",
      "Set agricultural theme",
      "Use gradient from #FF0000 to #0000FF",
      "Apply pastel colors",
      "Use corporate color scheme"
    ];
  }
}

export const colorMixin = new ColorMixin();
